import React, { useMemo, useState } from "react";

import PageLayout from "../../components/PageLayout";
import HeaderIconSvg from "../../icons/newicons/calculator-fill.svg";
import {
  ACCOUNT_MAPPINGS,
  CONNECTION,
  JOURNAL_SAMPLES,
  SOURCE_CARDS,
  SYNC_HISTORY,
} from "./sampleData";

import "./quickbooks.css";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "journal", label: "Journal Entries" },
  { key: "mapping", label: "Account Mapping" },
  { key: "history", label: "Sync History" },
];

function formatUsd(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
}

function statusBadge(status) {
  const cls =
    status === "Synced"
      ? "qb-badge-synced"
      : status === "Pending"
      ? "qb-badge-pending"
      : "qb-badge-failed";
  return <span className={`qb-badge ${cls}`}>{status}</span>;
}

const QuickBooks = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [journalKey, setJournalKey] = useState("costSheet");
  const [connected, setConnected] = useState(true);

  const journal = JOURNAL_SAMPLES[journalKey];

  const journalTotals = useMemo(() => {
    const debit = journal.lines.reduce((s, l) => s + (l.debit || 0), 0);
    const credit = journal.lines.reduce((s, l) => s + (l.credit || 0), 0);
    return { debit, credit };
  }, [journal]);

  return (
    <PageLayout
      icon={HeaderIconSvg}
      heading="QuickBooks Integration"
      hideSearchBar={true}
    >
      <div className="qb-page">
        <div className="qb-demo-banner">
          Demo UI — sample data only. QuickBooks OAuth &amp; API sync will be
          wired in a later phase.
        </div>

        <div className="qb-connect-card">
          <div className="qb-connect-left">
            <div className="qb-logo">QB</div>
            <div>
              {connected ? (
                <>
                  <div className="qb-status-pill is-connected">
                    <span className="qb-status-dot" />
                    Connected
                  </div>
                  <h2 className="qb-connect-title">{CONNECTION.companyName}</h2>
                  <p className="qb-connect-meta">
                    Realm ID {CONNECTION.realmId} · Connected{" "}
                    {CONNECTION.connectedAt} · Last sync {CONNECTION.lastSyncAt}
                  </p>
                </>
              ) : (
                <>
                  <h2 className="qb-connect-title">Connect QuickBooks Online</h2>
                  <p className="qb-connect-meta">
                    Link your QuickBooks company to post Cost Sheet, Settlement,
                    and Ounce journals as balanced debit/credit entries.
                  </p>
                </>
              )}
            </div>
          </div>
          <div className="qb-connect-actions">
            {connected ? (
              <>
                <button
                  type="button"
                  className="qb-btn qb-btn-ghost"
                  onClick={() => setActiveTab("history")}
                >
                  View Sync History
                </button>
                <button
                  type="button"
                  className="qb-btn qb-btn-danger-ghost"
                  onClick={() => setConnected(false)}
                >
                  Disconnect
                </button>
              </>
            ) : (
              <button
                type="button"
                className="qb-btn qb-btn-primary"
                onClick={() => setConnected(true)}
              >
                Connect to QuickBooks
              </button>
            )}
          </div>
        </div>

        <div className="qb-tabs">
          {TABS.map((tab) => (
            <div
              key={tab.key}
              className={`qb-tab ${activeTab === tab.key ? "is-active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </div>
          ))}
        </div>

        {activeTab === "overview" && (
          <>
            <h3 className="qb-section-title">What gets posted to QuickBooks</h3>
            <p className="qb-section-sub">
              CAT records become QuickBooks journal entries. Each posting is a
              balanced debit/credit pair so books stay in balance.
            </p>

            <div className="qb-source-grid">
              {SOURCE_CARDS.map((card) => (
                <div
                  key={card.key}
                  className={`qb-source-card ${
                    journalKey === card.key ? "is-selected" : ""
                  }`}
                  onClick={() => {
                    setJournalKey(card.key);
                    setActiveTab("journal");
                  }}
                >
                  <div className="qb-source-eyebrow">Sync source</div>
                  <h4 className="qb-source-title">{card.title}</h4>
                  <p className="qb-source-sub">{card.subtitle}</p>
                  <p className="qb-source-desc">{card.description}</p>
                  <div className="qb-source-trigger">
                    <strong>Trigger:</strong> {card.trigger}
                  </div>
                </div>
              ))}
            </div>

            <h3 className="qb-section-title">How it works</h3>
            <div className="qb-flow">
              <div className="qb-flow-step">
                <span className="qb-flow-num">1</span>
                Cost Sheet / Settlement / Ounce recorded in CAT
              </div>
              <span className="qb-flow-arrow">→</span>
              <div className="qb-flow-step">
                <span className="qb-flow-num">2</span>
                Map to QuickBooks accounts
              </div>
              <span className="qb-flow-arrow">→</span>
              <div className="qb-flow-step">
                <span className="qb-flow-num">3</span>
                Post balanced Debit / Credit journal
              </div>
              <span className="qb-flow-arrow">→</span>
              <div className="qb-flow-step">
                <span className="qb-flow-num">4</span>
                Visible in QuickBooks + Sync History
              </div>
            </div>
          </>
        )}

        {activeTab === "journal" && (
          <>
            <div className="qb-journal-toolbar">
              <div>
                <h3 className="qb-section-title" style={{ marginBottom: 4 }}>
                  Sample journal entry
                </h3>
                <p className="qb-section-sub" style={{ marginBottom: 0 }}>
                  Preview of the debit/credit lines CAT would post to
                  QuickBooks.
                </p>
              </div>
              <div className="qb-chip-row">
                {SOURCE_CARDS.map((card) => (
                  <button
                    key={card.key}
                    type="button"
                    className={`qb-chip ${
                      journalKey === card.key ? "is-active" : ""
                    }`}
                    onClick={() => setJournalKey(card.key)}
                  >
                    {card.title}
                  </button>
                ))}
              </div>
            </div>

            <div className="qb-panel">
              <div className="qb-panel-header">
                <div>
                  <h4 className="qb-panel-title">
                    {journal.id} · {journal.source}
                  </h4>
                  <p className="qb-panel-meta">
                    {journal.sourceRef}
                    <br />
                    {journal.memo}
                    <br />
                    {journal.customer} · {journal.facility} · {journal.date}
                  </p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#868e96",
                      textTransform: "uppercase",
                    }}
                  >
                    Document total
                  </div>
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: "#212529",
                      marginTop: 4,
                    }}
                  >
                    {formatUsd(journal.amount)}
                  </div>
                </div>
              </div>

              <table className="qb-table">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Description</th>
                    <th className="qb-num">Debit</th>
                    <th className="qb-num">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {journal.lines.map((line, idx) => (
                    <tr key={`${journal.id}-${idx}`}>
                      <td>{line.account}</td>
                      <td>{line.description}</td>
                      <td className="qb-num qb-debit">
                        {line.debit != null ? formatUsd(line.debit) : ""}
                      </td>
                      <td className="qb-num qb-credit">
                        {line.credit != null ? formatUsd(line.credit) : ""}
                      </td>
                    </tr>
                  ))}
                  <tr className="qb-totals-row">
                    <td colSpan={2}>Totals (must balance)</td>
                    <td className="qb-num qb-debit">
                      {formatUsd(journalTotals.debit)}
                    </td>
                    <td className="qb-num qb-credit">
                      {formatUsd(journalTotals.credit)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="qb-legend">
              <span>
                <strong className="qb-debit">Debit</strong> = increase asset /
                expense
              </span>
              <span>
                <strong className="qb-credit">Credit</strong> = increase
                liability / cash out / income offset
              </span>
            </div>
          </>
        )}

        {activeTab === "mapping" && (
          <>
            <h3 className="qb-section-title">Account mapping</h3>
            <p className="qb-section-sub">
              CAT source fields map to QuickBooks chart-of-accounts. Editable
              after real OAuth connect (demo shows defaults).
            </p>
            <div className="qb-panel">
              <table className="qb-table">
                <thead>
                  <tr>
                    <th>CAT Source</th>
                    <th>QuickBooks Account</th>
                    <th>Account Type</th>
                    <th>Side</th>
                  </tr>
                </thead>
                <tbody>
                  {ACCOUNT_MAPPINGS.map((row) => (
                    <tr key={row.id}>
                      <td>{row.catSource}</td>
                      <td>{row.qbAccount}</td>
                      <td>{row.qbAccountType}</td>
                      <td>
                        <span
                          className={
                            row.side === "Debit" ? "qb-debit" : "qb-credit"
                          }
                        >
                          {row.side}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === "history" && (
          <>
            <h3 className="qb-section-title">Sync history</h3>
            <p className="qb-section-sub">
              Each Cost Sheet, Settlement, or Ounce posting that was sent (or
              queued) to QuickBooks.
            </p>
            <div className="qb-panel">
              <table className="qb-table">
                <thead>
                  <tr>
                    <th>Sync ID</th>
                    <th>Source</th>
                    <th>Reference</th>
                    <th>QuickBooks Doc</th>
                    <th className="qb-num">Amount</th>
                    <th>Status</th>
                    <th>Synced At</th>
                  </tr>
                </thead>
                <tbody>
                  {SYNC_HISTORY.map((row) => (
                    <tr key={row.id}>
                      <td>{row.id}</td>
                      <td>{row.source}</td>
                      <td>{row.sourceRef}</td>
                      <td>{row.qbDoc}</td>
                      <td className="qb-num">{formatUsd(row.amount)}</td>
                      <td>{statusBadge(row.status)}</td>
                      <td>{row.syncedAt}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default QuickBooks;
